/* ==========================================================================
   Hause of Nu: hon.js (v2). Vanilla, no dependencies.
   Hooks are data-hon-* attributes so classes stay presentational.
   Modules: images, header, mobile menu, reveals, hero, gallery, variants, notes rail,
   quantity, countdown, launch switch, signup focus, bag bump, same-page anchors.
   Every module is idempotent: it runs on the document at load and on a section element
   when the theme editor fires shopify:section:load. Real add-to-bag is Dawn's
   <product-form>; the real signup is Shopify's customer form. Nothing here fakes either.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__honInit) return;
  window.__honInit = true;

  var doc = document;
  var root = doc.documentElement;
  root.classList.add('hon-js');
  window.__honReady = true;

  var $ = function (sel, ctx) { return (ctx || doc).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktopMq = window.matchMedia('(min-width: 1024px)');
  var designMode = function () {
    return !!(window.Shopify && window.Shopify.designMode) || root.classList.contains('shopify-design-mode');
  };
  /* one bound flag per element and module (an element can belong to two modules) */
  function once(el, key) {
    var attr = 'data-hon-bound-' + key;
    if (el.hasAttribute(attr)) return false;
    el.setAttribute(attr, '');
    return true;
  }
  /* find within ctx, including ctx itself */
  function within(sel, ctx) {
    var list = $$(sel, ctx);
    if (ctx && ctx.matches && ctx.matches(sel)) list.unshift(ctx);
    return list;
  }

  /* ---------------------------------------------------------------------
     Images: hide broken images so the gradient frame shows; mark loaded.
     --------------------------------------------------------------------- */
  function initImages(ctx) {
    within('.hon-root img', ctx).forEach(function (img) {
      if (!once(img, 'img')) return;
      var loaded = function () { img.classList.remove('is-missing'); img.classList.add('is-loaded'); };
      var missing = function () { img.classList.add('is-missing'); };
      if (img.complete) {
        if (img.naturalWidth > 0) loaded(); else if (img.currentSrc || img.src) missing();
      }
      img.addEventListener('load', loaded);
      img.addEventListener('error', missing);
    });
  }

  /* ---------------------------------------------------------------------
     Header: hairline once the page scrolls (one global listener).
     --------------------------------------------------------------------- */
  var scrollTicking = false;
  function updateHeaders() {
    var scrolled = window.scrollY > 24;
    $$('[data-hon-header]').forEach(function (h) { h.classList.toggle('is-scrolled', scrolled); });
    scrollTicking = false;
  }
  window.addEventListener('scroll', function () {
    if (!scrollTicking) { scrollTicking = true; window.requestAnimationFrame(updateHeaders); }
  }, { passive: true });

  /* ---------------------------------------------------------------------
     Mobile menu: dialog with focus trap, Esc closes, aria-expanded.
     --------------------------------------------------------------------- */
  var openMenuEl = null;
  var lastFocus = null;
  function menuParts(menu) {
    var header = menu.closest('[data-hon-header]') || doc;
    return { toggle: $('[data-hon-menu-toggle]', header), close: $('[data-hon-menu-close]', menu) };
  }
  function focusables(menu) {
    return $$('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', menu)
      .filter(function (el) { return el.getClientRects().length > 0; });
  }
  function onMenuKeydown(e) {
    var menu = openMenuEl;
    if (!menu) return;
    if (e.key === 'Escape') { e.preventDefault(); closeMenu(menu, true); return; }
    if (e.key !== 'Tab') return;
    var items = focusables(menu);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = doc.activeElement;
    if (e.shiftKey && (active === first || !menu.contains(active))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || !menu.contains(active))) { e.preventDefault(); first.focus(); }
  }
  function openMenu(menu, moveFocus) {
    if (openMenuEl && openMenuEl !== menu) closeMenu(openMenuEl, false);
    var parts = menuParts(menu);
    lastFocus = doc.activeElement;
    openMenuEl = menu;
    menu.classList.add('is-open');
    if (parts.toggle) parts.toggle.setAttribute('aria-expanded', 'true');
    root.classList.add('hon-menu-open');
    doc.addEventListener('keydown', onMenuKeydown);
    if (moveFocus !== false) {
      window.requestAnimationFrame(function () {
        var target = parts.close || focusables(menu)[0];
        if (target) target.focus();
      });
    }
  }
  function closeMenu(menu, restoreFocus) {
    if (!menu || !menu.classList.contains('is-open')) return;
    var parts = menuParts(menu);
    menu.classList.remove('is-open');
    if (parts.toggle) parts.toggle.setAttribute('aria-expanded', 'false');
    root.classList.remove('hon-menu-open');
    doc.removeEventListener('keydown', onMenuKeydown);
    if (openMenuEl === menu) openMenuEl = null;
    if (restoreFocus) {
      var target = parts.toggle && parts.toggle.getClientRects().length ? parts.toggle : lastFocus;
      if (target && typeof target.focus === 'function') target.focus();
    }
  }
  function initMenus(ctx) {
    within('[data-hon-menu]', ctx).forEach(function (menu) {
      if (!once(menu, 'menu')) return;
      var parts = menuParts(menu);
      if (parts.toggle) {
        parts.toggle.addEventListener('click', function () {
          if (menu.classList.contains('is-open')) closeMenu(menu, true); else openMenu(menu);
        });
      }
      if (parts.close) parts.close.addEventListener('click', function () { closeMenu(menu, true); });
      menu.addEventListener('click', function (e) {
        if (e.target.closest('[data-hon-menu-link]')) closeMenu(menu, false);
      });
    });
  }
  var onDesktop = function (mq) { if (mq.matches && openMenuEl) closeMenu(openMenuEl, false); };
  if (desktopMq.addEventListener) desktopMq.addEventListener('change', onDesktop);
  else if (desktopMq.addListener) desktopMq.addListener(onDesktop);

  /* ---------------------------------------------------------------------
     Reveals: fade + rise, staggered 80ms per [data-reveal-group].
     Always visible in the theme editor and with reduced motion.
     --------------------------------------------------------------------- */
  var revealObserver = null;
  function showAll(list) { list.forEach(function (el) { el.classList.add('is-in'); }); }
  function initReveal(ctx) {
    within('[data-reveal-group]', ctx).forEach(function (group) {
      $$('[data-reveal]', group).forEach(function (el, i) { el.style.setProperty('--hon-delay', (i * 80) + 'ms'); });
    });
    var items = within('[data-reveal]', ctx).filter(function (el) { return once(el, 'reveal'); });
    if (!items.length) return;
    if (designMode() || reduceMotion.matches || !('IntersectionObserver' in window)) { showAll(items); return; }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    }
    items.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------------------------------------------------------------------
     Hero: slow settle (scale 1.04 -> 1) once the portrait has loaded.
     --------------------------------------------------------------------- */
  function initHero(ctx) {
    within('[data-hon-hero]', ctx).forEach(function (hero) {
      if (!once(hero, 'hero')) return;
      var img = $('.hon-hero__img', hero);
      var settle = function () { window.requestAnimationFrame(function () { hero.classList.add('is-loaded'); }); };
      if (!img || img.complete) { settle(); return; }
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
    });
  }

  /* ---------------------------------------------------------------------
     Gallery: thumbnails crossfade the main image (aria-pressed).
     --------------------------------------------------------------------- */
  function showSlide(gallery, index) {
    if (!gallery) return;
    var slides = $$('[data-hon-slide]', gallery);
    var thumbs = $$('[data-hon-thumb]', gallery);
    if (index < 0 || index >= slides.length) return;
    slides.forEach(function (slide, j) {
      var active = j === index;
      slide.classList.toggle('is-active', active);
      if (active) slide.removeAttribute('aria-hidden'); else slide.setAttribute('aria-hidden', 'true');
      /* lazy images in hidden slides: load the one being shown now */
      if (active) { var img = $('img[loading="lazy"]', slide); if (img) img.loading = 'eager'; }
    });
    thumbs.forEach(function (t, j) { t.setAttribute('aria-pressed', String(j === index)); });
  }
  function initGallery(ctx) {
    within('[data-hon-gallery]', ctx).forEach(function (gallery) {
      if (!once(gallery, 'gallery')) return;
      $$('[data-hon-thumb]', gallery).forEach(function (thumb, i) {
        thumb.addEventListener('click', function () { showSlide(gallery, i); });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Variant select (only rendered for products with several variants):
     keeps the variant id, price, availability and gallery image in step.
     --------------------------------------------------------------------- */
  function initVariants(ctx) {
    within('[data-hon-variant-select]', ctx).forEach(function (select) {
      if (!once(select, 'variant')) return;
      var scope = select.closest('[data-hon-product]') || doc;
      var apply = function () {
        var opt = select.options[select.selectedIndex];
        if (!opt) return;
        var available = opt.getAttribute('data-available') === 'true';
        var idInput = $('form input[name="id"]', scope);
        if (idInput) {
          idInput.value = opt.value;
          idInput.disabled = !available;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        var price = $('[data-hon-price]', scope);
        if (price) price.textContent = opt.getAttribute('data-price');
        var compare = $('[data-hon-compare]', scope);
        if (compare) {
          var cmp = opt.getAttribute('data-compare');
          var hidden = $('.hon-visually-hidden', compare);
          compare.textContent = '';
          if (hidden) compare.appendChild(hidden);
          if (cmp) compare.appendChild(doc.createTextNode(cmp));
          compare.hidden = !cmp;
        }
        var submit = $('[data-hon-add]', scope);
        if (submit) {
          var label = $('span', submit);
          submit.disabled = !available;
          if (label) label.textContent = submit.getAttribute(available ? 'data-label-add' : 'data-label-soldout');
        }
        var media = opt.getAttribute('data-media');
        var gallery = $('[data-hon-gallery]', scope);
        if (gallery) {
          var slides = $$('[data-hon-slide]', gallery);
          for (var i = 0; i < slides.length; i++) {
            if ((media && slides[i].getAttribute('data-media-id') === media) || slides[i].getAttribute('data-shade') === opt.getAttribute('data-shade')) { showSlide(gallery, i); break; }
          }
        }
      };
      select.addEventListener('change', apply);
      apply();
    });
  }

  /* ---------------------------------------------------------------------
     Notes rail: a tab stop only when it actually scrolls.
     --------------------------------------------------------------------- */
  function syncRails() {
    $$('[data-hon-rail]').forEach(function (rail) {
      if (rail.scrollWidth > rail.clientWidth + 1) rail.setAttribute('tabindex', '0');
      else rail.removeAttribute('tabindex');
    });
  }
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(syncRails, 120);
  }, { passive: true });

  /* ---------------------------------------------------------------------
     Quantity stepper (respects min / max / step).
     --------------------------------------------------------------------- */
  function initQuantity(ctx) {
    within('[data-hon-qty]', ctx).forEach(function (qty) {
      if (!once(qty, 'qty')) return;
      var input = $('input', qty);
      if (!input) return;
      var buttons = $$('[data-hon-step]', qty);
      var min = function () { return parseInt(input.min, 10) || 1; };
      var max = function () { var m = parseInt(input.max, 10); return isNaN(m) ? Infinity : m; };
      var step = function () { return parseInt(input.step, 10) || 1; };
      var clamp = function (v) { return Math.min(max(), Math.max(min(), isNaN(v) ? min() : v)); };
      var sync = function () {
        var v = Number(input.value);
        buttons.forEach(function (b) {
          var dir = Number(b.getAttribute('data-hon-step'));
          b.disabled = (dir < 0 && v <= min()) || (dir > 0 && v >= max());
        });
      };
      buttons.forEach(function (b) {
        b.addEventListener('click', function () {
          var next = clamp((parseInt(input.value, 10) || min()) + Number(b.getAttribute('data-hon-step')) * step());
          if (String(next) !== input.value) {
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          sync();
        });
      });
      input.addEventListener('change', function () {
        var v = clamp(parseInt(input.value, 10));
        if (String(v) !== input.value) input.value = v;
        sync();
      });
      sync();
    });
  }

  /* ---------------------------------------------------------------------
     Launch countdown: "DDd HHh MMm", the done text once the launch passes.
     --------------------------------------------------------------------- */
  var countdownTimer = null;
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function tickLaunch(box) {
    var target = new Date(box.getAttribute('data-hon-target')).getTime();
    if (isNaN(target)) return;
    var diff = target - Date.now();
    if (diff <= 0) {
      var text = $('[data-hon-launch-text]', box);
      var done = box.getAttribute('data-hon-done');
      if (text && done && text.textContent !== done) text.textContent = done;
      box.removeAttribute('data-hon-target');
      return;
    }
    var d = Math.floor(diff / 864e5);
    var h = Math.floor((diff % 864e5) / 36e5);
    var m = Math.floor((diff % 36e5) / 6e4);
    var value = pad(d) + 'd ' + pad(h) + 'h ' + pad(m) + 'm';
    var el = $('[data-hon-countdown]', box);
    if (el && el.textContent !== value) el.textContent = value;
  }
  function initCountdown() {
    var boxes = $$('[data-hon-launch][data-hon-target]');
    boxes.forEach(tickLaunch);
    if (boxes.length && !countdownTimer) {
      countdownTimer = window.setInterval(function () {
        var all = $$('[data-hon-launch][data-hon-target]');
        if (!all.length) { window.clearInterval(countdownTimer); countdownTimer = null; return; }
        all.forEach(tickLaunch);
      }, 15000);
    }
  }

  /* ---------------------------------------------------------------------
     Launch switch: snippets/hon-launch-text marks before-launch text with the
     launch time; a cached page swaps to the after-launch text (and link) on time.
     --------------------------------------------------------------------- */
  var launchTimer = null;
  function swapLaunch() {
    var now = Date.now();
    var left = 0;
    $$('[data-hon-launch-at]').forEach(function (el) {
      if (now < Number(el.getAttribute('data-hon-launch-at')) * 1000) { left++; return; }
      var href = el.getAttribute('data-hon-launch-href');
      var link = href && el.closest('a');
      if (link) link.setAttribute('href', href);
      el.textContent = el.getAttribute('data-hon-launch-text');
      el.removeAttribute('data-hon-launch-at');
    });
    return left;
  }
  function initLaunch() {
    if (swapLaunch() && !launchTimer) {
      launchTimer = window.setInterval(function () {
        if (!swapLaunch()) { window.clearInterval(launchTimer); launchTimer = null; }
      }, 30000);
    }
  }

  /* ---------------------------------------------------------------------
     Signup: after Shopify's post-back, move focus to the success message.
     --------------------------------------------------------------------- */
  function initSignup(ctx) {
    within('[data-hon-signup-success]', ctx).forEach(function (msg) {
      if (!once(msg, 'signup')) return;
      if (ctx === doc) window.requestAnimationFrame(function () { msg.focus({ preventScroll: true }); });
    });
  }

  /* ---------------------------------------------------------------------
     Bag: a small bump when Dawn re-renders #cart-icon-bubble after an add.
     --------------------------------------------------------------------- */
  var bagObserver = null;
  function initBag() {
    var bag = doc.getElementById('cart-icon-bubble');
    if (!bag || !('MutationObserver' in window) || bag.hasAttribute('data-hon-bound-bag')) return;
    bag.setAttribute('data-hon-bound-bag', '');
    if (bagObserver) bagObserver.disconnect();
    bagObserver = new MutationObserver(function () {
      bag.classList.remove('is-bumped');
      void bag.offsetWidth;
      bag.classList.add('is-bumped');
    });
    bagObserver.observe(bag, { childList: true });
  }

  /* ---------------------------------------------------------------------
     Same-page anchors: "/#fragrance" on the homepage scrolls instead of reloading.
     --------------------------------------------------------------------- */
  doc.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var link = e.target.closest && e.target.closest('.hon-root a[href*="#"]');
    if (!link || link.target === '_blank') return;
    var url;
    try { url = new URL(link.href, window.location.href); } catch (err) { return; }
    if (!url.hash || url.hash.length < 2) return;
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
    var target;
    try { target = doc.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (err2) { return; }
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    if (window.history && window.history.pushState) window.history.pushState(null, '', url.hash);
  });

  /* ---------------------------------------------------------------------
     Init + theme editor
     --------------------------------------------------------------------- */
  function init(ctx) {
    ctx = ctx || doc;
    initImages(ctx);
    initMenus(ctx);
    initReveal(ctx);
    initHero(ctx);
    initGallery(ctx);
    initVariants(ctx);
    initQuantity(ctx);
    initSignup(ctx);
    initCountdown();
    initLaunch();
    initBag();
    syncRails();
    updateHeaders();
  }

  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });
  doc.addEventListener('shopify:section:unload', function (e) {
    var menu = $('[data-hon-menu].is-open', e.target);
    if (menu) closeMenu(menu, false);
    if (e.target.querySelector && e.target.querySelector('#cart-icon-bubble') && bagObserver) {
      bagObserver.disconnect();
      bagObserver = null;
    }
  });
  /* selecting a section reveals everything in it immediately */
  doc.addEventListener('shopify:section:select', function (e) { showAll(within('[data-reveal]', e.target)); });
  doc.addEventListener('shopify:section:deselect', function (e) {
    var menu = $('[data-hon-menu].is-open', e.target);
    if (menu) closeMenu(menu, false);
  });
  doc.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (!block || !block.matches) return;
    if (block.matches('details')) block.open = true;
    if (block.matches('[data-hon-slide]')) {
      var gallery = block.closest('[data-hon-gallery]');
      showSlide(gallery, $$('[data-hon-slide]', gallery).indexOf(block));
    }
    /* header link blocks live in the desktop nav; on small screens open the menu to show them */
    var header = block.closest('[data-hon-header]');
    if (header && !desktopMq.matches) {
      var menu = $('[data-hon-menu]', header);
      if (menu && !menu.classList.contains('is-open')) openMenu(menu, false);
    }
    showAll(within('[data-reveal]', block));
  });
  doc.addEventListener('shopify:block:deselect', function (e) {
    var header = e.target && e.target.closest && e.target.closest('[data-hon-header]');
    var menu = header && $('[data-hon-menu].is-open', header);
    if (menu) closeMenu(menu, false);
  });

  if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(syncRails);
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { init(doc); });
  else init(doc);
})();
